// Predictions API Client

import { API_ENDPOINTS, API_BASE_URL } from '../config';
import type {
  ApiResponse,
  SlotPrediction,
  AggregatedSlot,
  ActualVsExpected,
  ActualVsExpectedSummary,
  DesignedShift,
  ModelCoefficient,
  PredictionModel,
  PredictionAccuracy,
  GeneratePredictionsRequest,
  GeneratePredictionsResponse,
  TrainAllModelsResponse,
  SummaryResponse,
  SolverDecisionsResponse,
  PayrollPreviewResponse,
} from '../types/prediction-types';

// Helper for API calls
async function fetchApi<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// GET /api/predictions/slots (raw slot predictions)
export async function getSlots(params?: {
  start_date?: string;
  end_date?: string;
  limit?: number;
  category?: string;
  target?: string;
}): Promise<ApiResponse<SlotPrediction>> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.category) searchParams.set('category', params.category);
  if (params?.target) searchParams.set('target', params.target);

  const url = `${API_ENDPOINTS.predictions.slots}?${searchParams}`;
  return fetchApi<ApiResponse<SlotPrediction>>(url);
}

// GET /api/predictions/aggregated-slots (aggregated time segments)
export async function getAggregatedSlots(params?: {
  start_date?: string;
  end_date?: string;
  limit?: number;
}): Promise<ApiResponse<AggregatedSlot>> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const url = `${API_ENDPOINTS.predictions.slots.replace('/slots', '/aggregated-slots')}?${searchParams}`;
  return fetchApi<ApiResponse<AggregatedSlot>>(url);
}

// GET /api/predictions/actual-vs-expected (comparison data)
export async function getActualVsExpected(params?: {
  start_date?: string;
  end_date?: string;
  target?: 'revenue' | 'preparation_time';
  limit?: number;
}): Promise<ApiResponse<ActualVsExpected>> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);
  if (params?.target) searchParams.set('target', params.target);
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const url = `${API_ENDPOINTS.predictions.slots.replace('/slots', '/actual-vs-expected')}?${searchParams}`;
  return fetchApi<ApiResponse<ActualVsExpected>>(url);
}

// GET /api/predictions/actual-vs-expected-summary (aggregated by date - FAST!)
export async function getActualVsExpectedSummary(params?: {
  start_date?: string;
  end_date?: string;
}): Promise<ApiResponse<ActualVsExpectedSummary>> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);

  const url = `${API_ENDPOINTS.predictions.slots.replace('/slots', '/actual-vs-expected-summary')}?${searchParams}`;
  return fetchApi<ApiResponse<ActualVsExpectedSummary>>(url);
}

// GET /api/predictions/coefficients
export async function getCoefficients(params?: {
  model_id?: number;
  target?: string;
}): Promise<ApiResponse<ModelCoefficient>> {
  const searchParams = new URLSearchParams();
  if (params?.model_id) searchParams.set('model_id', params.model_id.toString());
  if (params?.target) searchParams.set('target', params.target);

  const url = `${API_ENDPOINTS.predictions.coefficients}?${searchParams}`;
  return fetchApi<ApiResponse<ModelCoefficient>>(url);
}

// GET /api/predictions/models
export async function getModels(params?: {
  target?: string;
  status?: string;
}): Promise<ApiResponse<PredictionModel>> {
  const searchParams = new URLSearchParams();
  if (params?.target) searchParams.set('target', params.target);
  if (params?.status) searchParams.set('status', params.status);

  const url = `${API_ENDPOINTS.predictions.models}?${searchParams}`;
  return fetchApi<ApiResponse<PredictionModel>>(url);
}

// GET /api/predictions/accuracy
export async function getAccuracy(params?: {
  target?: string;
  limit?: number;
}): Promise<ApiResponse<PredictionAccuracy>> {
  const searchParams = new URLSearchParams();
  if (params?.target) searchParams.set('target', params.target);
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const url = `${API_ENDPOINTS.predictions.accuracy}?${searchParams}`;
  return fetchApi<ApiResponse<PredictionAccuracy>>(url);
}

// GET /api/predictions/summary
export async function getSummary(params?: {
  start_date?: string;
  end_date?: string;
}): Promise<SummaryResponse> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);

  const url = `${API_ENDPOINTS.predictions.summary}?${searchParams}`;
  return fetchApi<SummaryResponse>(url);
}

// POST /api/predictions/generate
export async function generatePredictions(
  request: GeneratePredictionsRequest
): Promise<GeneratePredictionsResponse> {
  return fetchApi<GeneratePredictionsResponse>(API_ENDPOINTS.predictions.generate, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// POST /api/predictions/train-all
export async function trainAllModels(): Promise<TrainAllModelsResponse> {
  return fetchApi<TrainAllModelsResponse>(API_ENDPOINTS.predictions.trainAll, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// GET /api/shifts/designed - Get designed shift patterns
export async function getDesignedShifts(params?: {
  start_date?: string;
  end_date?: string;
}): Promise<ApiResponse<DesignedShift>> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);

  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/designed')}?${searchParams}`;
  return fetchApi<ApiResponse<DesignedShift>>(url);
}

// POST /api/shifts/designed/select - Select a shift pattern
export async function selectShiftPattern(pattern_id: number): Promise<{ success: boolean; pattern_id: number; date: string }> {
  const url = API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/designed/select');
  return fetchApi<{ success: boolean; pattern_id: number; date: string }>(url, {
    method: 'POST',
    body: JSON.stringify({ pattern_id }),
  });
}

// GET /api/shifts/optimize - Generate shift patterns
export async function generateShiftPatterns(params: {
  start_date: string;
  end_date: string;
  keep_existing?: boolean;  // If true, skip dates that already have designed shifts
}): Promise<any> {
  const searchParams = new URLSearchParams();
  searchParams.set('start', params.start_date);
  searchParams.set('end', params.end_date);
  if (params.keep_existing) {
    searchParams.set('keep_existing', 'true');
  }

  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/optimize')}?${searchParams}`;
  return fetchApi<any>(url);
}

// GET /api/shifts/assigned - Get assigned shifts
export async function getAssignedShifts(start_date: string, end_date: string): Promise<{ shifts: any[] }> {
  const searchParams = new URLSearchParams();
  searchParams.set('start_date', start_date);
  searchParams.set('end_date', end_date);

  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/assigned')}?${searchParams}`;
  return fetchApi<{ shifts: any[] }>(url);
}

// POST /api/shifts/export-to-square - Export week to Square
export async function exportWeekToSquare(start_date: string, end_date: string): Promise<{ success: boolean; exported_count: number }> {
  const url = API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/export-to-square');
  return fetchApi<{ success: boolean; exported_count: number }>(url, {
    method: 'POST',
    body: JSON.stringify({ start_date, end_date }),
  });
}

// POST /api/shifts/assign_shifts - Get comprehensive summary (keep_existing=true)
export async function getAssignedShiftsSummary(start_date: string, end_date: string): Promise<any> {
  // Calculate weeks from date range
  const start = new Date(start_date);
  const end = new Date(end_date);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const weeks = Math.ceil(diffDays / 7);

  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/assign_shifts')}`;
  return fetchApi<any>(url, {
    method: 'POST',
    body: JSON.stringify({
      start_date: start_date,
      weeks: weeks,
      keep_existing: true, // Don't regenerate, just return summary
      run_optimize: false, // Don't run optimization
    }),
  });
}

// GET /api/shifts-optimize/default-date-range - Get default date range
export async function getDefaultDateRange(): Promise<{
  start_date: string;
  end_date: string;
  last_published_shift: string | null;
}> {
  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts-optimize/default-date-range')}`;
  return fetchApi<any>(url);
}

// POST /api/shifts/assign_shifts - Run scheduling algorithm
export async function generateSchedule(params: {
  start_date: string;
  weeks?: number;
  keep_existing?: boolean;
}): Promise<any> {
  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/assign_shifts')}`;
  return fetchApi<any>(url, {
    method: 'POST',
    body: JSON.stringify({
      start_date: params.start_date,
      weeks: params.weeks || 4,
      keep_existing: params.keep_existing || false,
    }),
  });
}

// GET /api/shifts/export-status - Check if week is exported to Square
export async function getWeekExportStatus(week_start: string): Promise<any> {
  const searchParams = new URLSearchParams();
  searchParams.set('week_start', week_start);

  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/export-status')}?${searchParams}`;
  return fetchApi<any>(url);
}

// POST /api/shifts/export-week - Export specific week to Square
export async function exportWeek(week_start: string): Promise<any> {
  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/export-week')}`;
  return fetchApi<any>(url, {
    method: 'POST',
    body: JSON.stringify({ week_start }),
  });
}

// GET /api/shifts-optimize/config - Get optimizer configuration
export async function getOptimizerConfig(): Promise<Record<string, {
  value: number;
  description: string;
  updated_at: string;
}>> {
  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts-optimize/config')}`;
  return fetchApi<any>(url);
}

// POST /api/shifts-optimize/config/update - Update optimizer configuration
export async function updateOptimizerConfig(key: string, value: number): Promise<{
  success: boolean;
  key: string;
  value: number;
  updated_at: string;
}> {
  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts-optimize/config/update')}?key=${encodeURIComponent(key)}&value=${value}`;
  return fetchApi<any>(url, {
    method: 'POST',
  });
}

// GET /api/shifts/solver_decisions - Get solver decision breakdowns
export async function getSolverDecisions(params: {
  week_start?: string;
  start_date?: string;
  end_date?: string;
}): Promise<SolverDecisionsResponse> {
  const searchParams = new URLSearchParams();
  if (params.week_start) searchParams.set('week_start', params.week_start);
  if (params.start_date) searchParams.set('start_date', params.start_date);
  if (params.end_date) searchParams.set('end_date', params.end_date);

  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/shifts/solver_decisions')}?${searchParams}`;
  return fetchApi<SolverDecisionsResponse>(url);
}

// GET /api/config?prefix=scheduling.solver - Get config values with prefix
export async function getConfigByPrefix(prefix: string): Promise<Record<string, {
  value: number;
  description: string;
  updated_at: string;
}>> {
  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/config')}?prefix=${encodeURIComponent(prefix)}`;
  return fetchApi<any>(url);
}

// POST /api/config - Update config value
export async function updateConfig(key: string, value: string | number): Promise<{
  success: boolean;
  key: string;
  value: number;
  updated_at: string;
}> {
  const url = `${API_ENDPOINTS.predictions.slots.replace('/predictions/slots', '/config')}`;
  return fetchApi<any>(url, {
    method: 'POST',
    body: JSON.stringify({ key, value: String(value) }),
  });
}

// GET /payroll-preview - Get payroll data for a date range
export async function getPayrollPreview(params?: {
  start_date?: string;
  end_date?: string;
}): Promise<PayrollPreviewResponse> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);

  // Use consistent pattern: start with base URL and build path
  const url = `${API_BASE_URL}/payroll-preview?${searchParams}`;
  return fetchApi<PayrollPreviewResponse>(url);
}
